import React from 'react';
import MediaQuery from 'react-responsive';
import Infinite from 'react-infinite';
import PropTypes from 'prop-types';
import querystring from 'querystring';
import fetch from 'isomorphic-fetch';
import uniq from 'lodash/uniq';
import {
  CaptionText,
  HeadingText,
  Icon,
  Button,
  Link,
  Row,
  Col,
  grey600,
} from '@paypalcorp/pp-react';
import glamorous from 'glamorous';

import * as analytics from '../lib/analytics';

const SearchResultDonateButton = glamorous(Button)({
  position: 'absolute !important',
  right: 0,
  marginTop: '65px',
  marginRight: '15px',
});

const SearchResultCharityTitle = glamorous(HeadingText)({
  left: '150px',
  right: '250px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '600px',
  '@media(max-width: 950px)': {
    maxWidth: '450px',
  },
  '@media(max-width: 768px)': {
    left: '70px',
    right: '0px',
    maxWidth: '200px',
  },
});

require('es6-promise').polyfill();

const spinerStyle = {
  textAlign: 'center',
  padding: '15px 0px 10px 0px',
  width: '200px',
  height: '60px',
  margin: 'auto',
};

export default class extends React.Component {
  static contextTypes = {
    csrf: PropTypes.string,
  };

  static propTypes = {
    query: PropTypes.object,
    charities: PropTypes.array,
    states: PropTypes.object,
    searchUrl: PropTypes.string,
    charityUrl: PropTypes.string,
  };

  state = {
    page: 1,
    isInfiniteLoading: false,
    charities: this.props.charities || [],
    infiniteLoadBeginEdgeOffset: 50,
  };

  handleLoad = () => {
    this.setState({
      isInfiniteLoading: true,
    });

    const nextPage = this.state.page + 1;
    const paramsObj = Object.assign({}, this.props.query, { page: nextPage });
    const params = querystring.stringify(paramsObj);
    const url = `${this.props.searchUrl}?${params}`;

    fetch(url)
      .then((response) => {
        return response.json();
      })
      .then((result) => {
        const charities = result.charities;
        if (charities.length > 0) {
          this.setState({
            page: nextPage,
            isInfiniteLoading: false,
            charities: this.state.charities.concat(charities),
          });
        } else {
          this.setState({
            isInfiniteLoading: false,
            infiniteLoadBeginEdgeOffset: undefined,
          });
        }
      });
  };

  elementInfiniteLoad = () => {
    return <div style={spinerStyle} className={'hasSpinner'} />;
  };

  handleAnalyticsCharityLink = (npid) => {
    analytics.trackLinkWithData(
      'charityLink',
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      { crid: npid },
      'link'
    );
  };

  handleAnalyticsStateLink = (npid) => {
    analytics.trackLinkWithData(
      'stateLink',
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      { crid: npid },
      'link'
    );
  };

  handleAnalyticsCharityDonate = (npid) => {
    analytics.trackLinkWithData(
      'DonateButton',
      this.props.fptiInfo.pgrp,
      this.props.fptiInfo.page,
      { crid: npid },
      'link'
    );
  };

  categoryList = (npid, categories) =>
    categories.map((category, categoryIndex) => {
      if (categoryIndex === categories.length - 1) {
        return (
          <Link
            style={{ paddingRight: 0, paddingLeft: 0 }}
            key={`${npid}-${categoryIndex}`}
            href={`${this.props.resources.searchLink}?category=${category.value}`}
          >
            {category.label}
          </Link>
        );
      } else {
        return (
          <>
            <Link
              style={{ paddingRight: 0, paddingLeft: 0 }}
              key={`${npid}-${categoryIndex}`}
              href={`${this.props.resources.searchLink}?category=${category.value}`}
            >
              {category.label}
            </Link>
            &#44;
          </>
        );
      }
    });

  renderItemRow = ({
    charity,
    categories,
    npid,
    index,
    logoUrl,
    cityState,
  } = {}) => {
    return (
      <Row key={`item-${npid}-${index}`} className='item'>
        <Col xs='auto'>
          <div
            className={'avatar'}
            onClick={() =>
              window.open(`${this.props.charityUrl}/${npid}`, '_self')
            }
            style={{
              background: `url(${logoUrl}) no-repeat center, center`,
              backgroundSize: 'contain',
            }}
          />
        </Col>
        <Col xs={6}>
          <div className='item-desc'>
            <Link
              style={{ paddingRight: 0, paddingLeft: 0 }}
              onClick={this.handleAnalyticsCharityLink.bind(this, npid)}
              href={`${this.props.charityUrl}/${npid}`}
              secondary
              size='xl3'
              className='row-title'
            >
              <MediaQuery minWidth={769}>
                <SearchResultCharityTitle as='h3' size='md' regular={true}>
                  {charity.name}
                </SearchResultCharityTitle>
              </MediaQuery>
              <MediaQuery maxWidth={768}>
                <SearchResultCharityTitle as='h3' size='xs' regular={true}>
                  {charity.name}
                </SearchResultCharityTitle>
              </MediaQuery>
            </Link>
            <MediaQuery minWidth={769}>
              {this.categoryList(npid, categories)}
            </MediaQuery>
            <MediaQuery maxWidth={768}>
              <div className='row-category'>
                {this.categoryList(npid, categories)}
              </div>
            </MediaQuery>
            {cityState}
          </div>
        </Col>
        <Col>
          <MediaQuery minWidth={769}>
            <SearchResultDonateButton
              as='a'
              id={`nemo_searchResultsDonate-${index}`}
              onClick={this.handleAnalyticsCharityDonate.bind(this, npid)}
              href={`${this.props.charityUrl}/${npid}`}
            >
              {this.props.i18n('label.donate-button')}
            </SearchResultDonateButton>
          </MediaQuery>

          <MediaQuery maxWidth={768}>
            <span className='item-button-mobile'>
              <Icon name='chevron-right' size='md' />
            </span>
          </MediaQuery>
        </Col>
      </Row>
    );
  };

  renderList = (charities) => {
    const items = charities
      .filter((charity) => !!charity)
      .map((charity, index) => {
        const npid = charity.nonprofit_id;
        // default logo for fallback case
        const logoUrl =
          charity.logo_url ||
          `https://${this.props.cdnHost}/images/Cause/ic_donate.svg`;
        const address = charity.address ? charity.address : {};

        let categories;
        if (Array.isArray(charity.cause_area)) {
          const causeAreaIDs = uniq(charity.cause_area.map((ca) => ca.id));
          categories = causeAreaIDs.map((id) => ({
            label: this.props.i18n(`label.category-${id}`),
            value: String(id),
          }));
        } else {
          categories = [];
        }

        const cityState = (
          <CaptionText className='row-description' style={{ color: grey600 }}>
            {[address.city, address.state]
              .filter((x) => x && x.trim())
              .join(', ')}
          </CaptionText>
        );

        return (
          <>
            <MediaQuery minWidth={769}>
              {this.renderItemRow({
                charity,
                categories,
                npid,
                index,
                logoUrl,
                cityState,
              })}
            </MediaQuery>
            <MediaQuery maxWidth={769}>
              <Link
                style={{ padding: '0' }}
                className='tapable-field'
                onClick={this.handleAnalyticsCharityDonate.bind(this, npid)}
                href={`${this.props.charityUrl}/${npid}`}
              >
                {this.renderItemRow({
                  charity,
                  categories,
                  npid,
                  index,
                  logoUrl,
                  cityState,
                })}
              </Link>
            </MediaQuery>
          </>
        );
      });

    return (
      <div>
        <MediaQuery minWidth={769}>
          <Infinite
            elementHeight={167}
            containerHeight={167 * this.state.charities.length}
            isInfiniteLoading={this.state.isInfiniteLoading}
            infiniteLoadBeginEdgeOffset={this.state.infiniteLoadBeginEdgeOffset}
            loadingSpinnerDelegate={this.elementInfiniteLoad()}
            timeScrollStateLastsForAfterUserScrolls={1000}
            useWindowAsScrollContainer
            onInfiniteLoad={this.handleLoad}
          >
            {items}
          </Infinite>
        </MediaQuery>
        <MediaQuery maxWidth={768}>
          <Infinite
            elementHeight={111}
            containerHeight={111 * this.state.charities.length}
            isInfiniteLoading={this.state.isInfiniteLoading}
            infiniteLoadBeginEdgeOffset={this.state.infiniteLoadBeginEdgeOffset}
            loadingSpinnerDelegate={this.elementInfiniteLoad()}
            timeScrollStateLastsForAfterUserScrolls={1000}
            useWindowAsScrollContainer
            onInfiniteLoad={this.handleLoad}
          >
            {items}
          </Infinite>
        </MediaQuery>
      </div>
    );
  };

  render() {
    var charityList;
    if (this.state.charities.length === 0) {
      charityList = (
        <div>
          <HeadingText as='h5' size='sm'>
            {this.props.i18n('label.no-search-result')}
          </HeadingText>
        </div>
      );
    } else {
      charityList = this.renderList(this.state.charities);
    }

    return <div className={'list'}>{charityList}</div>;
  }
}
